import type { JSX } from 'react';

import { HelmetProvider } from 'react-helmet-async';
import { createBrowserRouter, matchPath, NavigationType, RouterProvider } from 'react-router';

import { WindowPageStateProvider } from 'src/shared/pageState';
import { PrefetchProvider } from 'src/shared/prefetch';
import type { Session } from 'src/shared/session';

import { createClientRouteLoaderFactory } from 'src/app/app/router';
import { createLoadingContext } from 'src/app/app/topLoadingBar';

import { createRoutes } from './createRoutes';
import { APP_ROUTES } from './index.ssr';
import type { AppOptions } from './types';

export function createClientApp(options: AppOptions): () => JSX.Element | null {
	if (options.ssr) {
		throw new Error('Invalid AppOptions');
	}

	const { prefetchedUserData, prefetchedData, prefetchedSession, session, sessionAvailableAtFirstRender } = options;

	const context: { session?: Session } = sessionAvailableAtFirstRender ? { session } : {};
	const loadingContext = createLoadingContext();

	const router = createBrowserRouter(
		createRoutes({
			context,
			loadingContext,
			loaderFactory: createClientRouteLoaderFactory({ context, ...options }),
			...options,
		}),
		{
			async dataStrategy({ matches }) {
				const matchesToLoad = matches.filter((m) => m.shouldCallHandler());
				if (matchesToLoad.length === 0) {
					return {};
				}
				loadingContext.start();
				let redirected = false;
				try {
					const results = await Promise.all(matchesToLoad.map(async (match) => match.resolve()));
					redirected = results.some(({ result }) => [301, 307].includes((result as Response).status));
					return results.reduce(
						(acc, result, i) =>
							Object.assign(acc, {
								[matchesToLoad[i].route.id]: result,
							}),
						{},
					);
				} finally {
					if (!redirected) {
						// if there was a redirect, we want to conserve the ongoing loading bar state
						loadingContext.complete();
					}
				}
			},
			// this is necessary to make sure the internal state is set correctly
			// so that "shouldRevalidate" is called at the first page change
			// TODO find a way to simplify this logic
			hydrationData: (() => {
				if (!options.ssrContext?.route) return undefined;
				const routeConfig = APP_ROUTES.find((route) => route.id === options.ssrContext?.route?.id);
				if (!routeConfig) return undefined;
				const { path } = routeConfig;
				const paths = Array.isArray(path) ? path : [path];
				if (paths.length === 1) {
					return {
						loaderData: { [routeConfig.id]: { prefetchedUserData, prefetchedData, prefetchedSession } },
					};
				}
				const idx = paths.findIndex((p) => matchPath(p, window.location.pathname));
				if (idx < 0) return undefined;
				return {
					// this id matches what is being set in createRoutes.tsx
					loaderData: { [`${routeConfig.id}:${idx}`]: { prefetchedUserData, prefetchedData, prefetchedSession } },
				};
			})(),
		},
	);

	// temporarily updating a memory history for the sake of Google Analytics and DS Modals
	// which are currently using the history object to listen to route changes
	// TODO: we should consider using a different approach for both
	router.subscribe((state) => {
		if (state.navigation.state !== 'idle') return;
		const url = state.location.pathname + state.location.search;
		if (state.historyAction === NavigationType.Push) {
			options.history.push(url);
		} else if (state.historyAction === NavigationType.Replace) {
			options.history.replace(url);
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		} else if (state.historyAction === NavigationType.Pop) {
			options.history.setEntries([url], 0);
		}
	});

	return function ClientApp(): JSX.Element | null {
		return (
			<WindowPageStateProvider>
				<HelmetProvider>
					<PrefetchProvider
						prefetchedData={options.prefetchedData}
						prefetchedUserData={options.prefetchedUserData}
						prefetchedSession={options.prefetchedSession}
						clearAfterFirstRender
					>
						<RouterProvider router={router} />
					</PrefetchProvider>
				</HelmetProvider>
			</WindowPageStateProvider>
		);
	};
}
